import moment from 'moment'
import { setBasket, setRequest } from 'Rentlio/state/actions/ui'
import {
  ADD_ITEM_TO_BASKET,
  CLEAR_BASKET,
  REMOVE_ITEM_FROM_BASKET,
  UPDATE_SERVICE_QUANTITY,
  FETCH_AVAILABILITY_COMPLETED
} from 'Rentlio/state/types'
import rentlioLocalStorage from 'Rentlio/helper/rentlioLocalStorage'
import { isDateInPast } from 'Rentlio/helper/isDateInPast'

const BASKET_STORAGE_KEY = 'basket'
const BASKET_EXPIRY_STORAGE_KEY = 'basket-expiry'
const REQUEST_STORAGE_KEY = 'request'

const clearBasketStorage = store => {
  rentlioLocalStorage.remove(BASKET_STORAGE_KEY, store)
  rentlioLocalStorage.remove(REQUEST_STORAGE_KEY, store)
  rentlioLocalStorage.remove(BASKET_EXPIRY_STORAGE_KEY, store)
}

const basketPersister = store => next => action => {
  // dont break the normal flow - this has to be called
  next(action)

  // check we are executing logic only on basket related action
  const { type } = action
  if (
    type !== ADD_ITEM_TO_BASKET &&
    type !== REMOVE_ITEM_FROM_BASKET &&
    type !== CLEAR_BASKET &&
    type !== UPDATE_SERVICE_QUANTITY
  ) {
    return
  }

  const { basket, request } = store.getState()

  rentlioLocalStorage.add(BASKET_STORAGE_KEY, JSON.stringify(basket), store)
  rentlioLocalStorage.add(BASKET_EXPIRY_STORAGE_KEY, moment().add(1, 'days').unix(), store)
  if (basket?.length > 0) {
    rentlioLocalStorage.add(REQUEST_STORAGE_KEY, JSON.stringify(request), store)
  } else {
    rentlioLocalStorage.remove(REQUEST_STORAGE_KEY, store)
  }
}

// This middleware runs on every Redux action, but we're only interested in the FETCH_AVAILABILITY_COMPLETED action.
//
// Initially, we load request data from localStorage (see function below), which triggers a FETCH_AVAILABILITY action.
// We then use the availability data to check if the basket stored in localStorage is still valid.
//
// If the stored basket is no longer available (e.g., items are unavailable), we clear both the basket and the request data from localStorage.
//
// Persisting the basket in localStorage is critical for supporting Google/Apple Pay,
// because those payment methods involve a redirect to an external URL.
// After successful payment, the user is returned to RBE to complete the reservation,
// and we rely on the basket from localStorage to resume the process.
export const updateBasketFromStorage = store => next => action => {
  // dont break the normal flow - this has to be called
  next(action)

  // check we are executing logic only when avvailability is fetched
  const { type } = action
  if (type !== FETCH_AVAILABILITY_COMPLETED) {
    return
  }

  // check if basket in localstorage is still valid (basket is valid 24h after saving to localstorage)
  const basketExpiry = rentlioLocalStorage.get(BASKET_EXPIRY_STORAGE_KEY, store)

  if (!basketExpiry || basketExpiry < moment().unix()) {
    clearBasketStorage(store)
  }

  const { unitTypes, request: stateRequest } = store.getState()

  const basketStorage = JSON.parse(rentlioLocalStorage.get(BASKET_STORAGE_KEY), store)

  if (basketStorage?.length > 0) {
    const requestStorage = JSON.parse(rentlioLocalStorage.get(REQUEST_STORAGE_KEY), store)

    if (
      requestStorage.from === stateRequest.from &&
      requestStorage.to === stateRequest.to &&
      !isDateInPast(moment.unix(requestStorage.from))
    ) {
      // Check if any rate, unit type or unit availability became unavailable in the meantime to prevent overbooking
      const isBookingUnavailable = basketStorage?.some(item => {
        const unitType = unitTypes.find(unitTypeItem => unitTypeItem.unitTypeId === item.unitTypeId)
        if (typeof unitType === 'undefined' || !unitType.available) {
          return true
        }

        const rate = unitType.rates.find(currentRate => currentRate.ratePlanId === item.ratePlanId)

        if (typeof rate === 'undefined' || !rate.isBookable) {
          return true
        }
        if (item.quantity > unitType.availableUnits) {
          return true
        }
      })

      // Get from storage only if isBookingUnavailable is false
      if (isBookingUnavailable === false) {
        basketStorage && store.dispatch(setBasket(basketStorage))
      } else {
        rentlioLocalStorage.remove(BASKET_STORAGE_KEY, store)
        rentlioLocalStorage.remove(REQUEST_STORAGE_KEY, store)
      }
    }
  }
}

// This function runs on RBE load. It checks if request data exists in localStorage.
// If found, it populates the Redux state with that request.
//
// The request data is essential for constructing the receipt, as it works in combination with the basket.
//
// After restoring the request to the state, we fetch fresh availability data
// to verify whether the basket from localStorage is still valid.
export const updateRequestFromStorage = store => {
  // When returning from the SaferPay payment page the reservation is booked from the
  // snapshot stored before the redirect, and the basket and request are restored from
  // that same snapshot - skip the restore here so no availability refetch happens
  const urlParams = new URLSearchParams(window.location.search)

  if (urlParams.get('saferpay-payment-page-redirect') === 'true') {
    return
  }

  const requestStorage = JSON.parse(rentlioLocalStorage.get(REQUEST_STORAGE_KEY, store))

  if (requestStorage && isDateInPast(moment.unix(requestStorage.from))) {
    clearBasketStorage(store)
  } else {
    requestStorage && store.dispatch(setRequest(requestStorage))
  }
}

export default basketPersister
